import React from 'react';
import {
	TextField,
	FormControlLabel,
	Checkbox,
	Switch,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	SelectChangeEvent,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import type { FormData } from '../../../types';

interface ProductOptionsProps {
	formData: FormData;
	setFormData: React.Dispatch<React.SetStateAction<FormData>>;
	disabled?: boolean;
}

const ProductOptions: React.FC<ProductOptionsProps> = ({ formData, setFormData, disabled }) => {
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value, checked, type } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: type === 'checkbox' ? checked : value,
		}));
	};

	// Add this specific handler for Select components
	const handleSelectChange = (e: SelectChangeEvent<string>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleInquiryToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
		const isChecked = e.target.checked;
		setFormData((prev) => ({
			...prev,
			isInquiry: isChecked,
			colorVariants: [],
			isColorVariantAvailable: false,
		}));
	};

	const handleWholesaleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
		const checked = e.target.checked;
		setFormData((prev) => ({
			...prev,
			isWholesale: checked,
			wholesaleFields: checked ? prev.wholesaleFields : [],
			isInquiry: false,
		}));
	};

	const handleBulkDiscountToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
		const checked = e.target.checked;
		setFormData((prev) => ({
			...prev,
			isBulkDiscount: checked,
			buldDiscountsData: checked ? prev.buldDiscountsData : [],
			isInquiry: false,
		}));
	};

	const handleColorVariantToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFormData((prev) => ({
			...prev,
			isColorVariantAvailable: e.target.checked,
		}));
	};

	return (
		<>
			<Grid size={{ xs: 12 }}>
				<FormControlLabel
					disabled={disabled}
					sx={{
						margin: 0,
						'& .MuiFormControlLabel-label': {
							fontSize: { xs: '14px', sm: '16px', md: '18px' },
						},
					}}
					control={
						<Switch
							checked={formData.isInquiry}
							onChange={handleInquiryToggle}
							disabled={formData.isWholesale || formData.isBulkDiscount}
						/>
					}
					label="Is Enquiry"
				/>
				{!formData.isInquiry && !formData.isColorVariantAvailable && (
					<FormControlLabel
						disabled={disabled}
						sx={{ margin: '0px' }}
						control={
							<Switch
								checked={formData.isStickoutPrice}
								onChange={(e) =>
									setFormData((prev) => ({
										...prev,
										isStickoutPrice: e.target.checked,
									}))
								}
							/>
						}
						label="Strike out price?"
					/>
				)}
			</Grid>

			{!formData.isInquiry && (
				<>
					<Grid size={{ xs: 12 }}>
						<TextField
							disabled={disabled}
							sx={{ margin: '0px' }}
							size="small"
							label="Stock"
							name="quantity"
							type="number"
							value={formData.quantity}
							onChange={handleChange}
							// required
							fullWidth
							margin="normal"
							// inputProps={{ min: '1' }}
							InputProps={{
								sx: {
									'& .MuiInputBase-input': {
										fontSize: { xs: 14, sm: 16, md: 18 }, // responsive font size
									},
								},
							}}
						/>
					</Grid>
					<Grid size={{ xs: 12 }}>
						<FormControlLabel
							disabled={disabled}
							control={
								<Checkbox
									name="bestSelling"
									checked={formData.bestSelling}
									onChange={handleChange}
								/>
							}
							label="Best Selling"
						/>
						<FormControlLabel
							disabled={disabled}
							control={
								<Checkbox
									name="trending"
									checked={formData.trending}
									onChange={handleChange}
								/>
							}
							label="Trending"
						/>
					</Grid>

					{formData.isStickoutPrice && (
						<Grid size={{ xs: 12 }}>
							<TextField
								disabled={disabled}
								sx={{ margin: '0px' }}
								size="small"
								label="Strikeout Price"
								name="strickoutPrice"
								type="number"
								value={formData.strickoutPrice}
								onChange={handleChange}
								required
								fullWidth
								margin="normal"
								inputProps={{ min: '1' }}
								placeholder="Enter the strikeout price"
							/>
						</Grid>
					)}

					<Grid size={{ xs: 12, sm: 4, md: 4 }}>
						<TextField
							sx={{ margin: '0px' }}
							size="small"
							label="Product Price"
							variant="outlined"
							type="number"
							fullWidth
							name="price"
							placeholder="0"
							value={formData.price}
							// onChange={(e) =>
							// 	setFormData((prev) => ({
							// 		...prev,
							// 		price: Number(e.target.value),
							// 	}))
							// }
							onChange={handleChange}
							inputProps={{ min: '1' }}
							disabled={formData.isColorVariantAvailable || disabled}
						/>
					</Grid>
					<Grid size={{ xs: 12, sm: 4, md: 4 }}>
						<TextField
							sx={{ margin: '0px' }}
							size="small"
							label="Weight"
							name="weight"
							type="number"
							value={formData.weight}
							onChange={handleChange}
							placeholder="0"
							required
							fullWidth
							inputProps={{ min: '1' }}
							disabled={disabled}
						/>
					</Grid>
					<Grid size={{ xs: 12, sm: 4, md: 4 }}>
						<FormControl fullWidth required disabled={disabled}>
							<InputLabel id="weightUnit-label">Weight Unit</InputLabel>
							<Select
								size="small"
								labelId="weightUnit-label"
								id="weightUnit"
								name="weightUnit"
								value={formData.weightUnit}
								onChange={handleSelectChange}
								// {/* Use the new handler here */}
								label="Weight Unit"
							>
								<MenuItem value="kg">kg</MenuItem>
								<MenuItem value="g">g</MenuItem>
								<MenuItem value="lb">lb</MenuItem>
								<MenuItem value="oz">oz</MenuItem>
							</Select>
						</FormControl>
					</Grid>

					<Grid size={{ xs: 12 }}>
						<FormControlLabel
							disabled={disabled}
							sx={{
								margin: 0,
								'& .MuiFormControlLabel-label': {
									fontSize: { xs: '14px', sm: '16px', md: '18px' }, // responsive font size
								},
							}}
							control={
								<Switch
									checked={formData.isWholesale}
									onChange={handleWholesaleToggle}
								/>
							}
							label="Is Wholesale"
						/>
						<FormControlLabel
							sx={{
								margin: 0,
								'& .MuiFormControlLabel-label': {
									fontSize: { xs: '14px', sm: '16px', md: '18px' }, // responsive font size
								},
							}}
							disabled={disabled}
							control={
								<Switch
									checked={formData.isBulkDiscount}
									onChange={handleBulkDiscountToggle}
								/>
							}
							label="Is Bulk Discount"
						/>
						<FormControlLabel
							sx={{
								margin: 0,
								'& .MuiFormControlLabel-label': {
									fontSize: { xs: '14px', sm: '16px', md: '18px' }, // responsive font size
								},
							}}
							disabled={disabled}
							control={
								<Switch
									checked={formData.isColorVariantAvailable}
									onChange={handleColorVariantToggle}
								/>
							}
							label="Is Color Variant Available"
						/>
					</Grid>
				</>
			)}
		</>
	);
};

export default ProductOptions;
